<template>
    <vx-modal v-model:visible="visible" size="lg" :padding="false">
        <page-select-group
            v-model="groups"
            :filters="{
                in_org_unit: 0,
            }"
            :statuses="GROUP_STATUSES_CONFIGURABLE"
        >
            <template v-slot:header> Add Groups </template>
            <template v-slot:subheader>
                Add multiple groups to <strong>{{ props.orgUnit.name }}</strong>
            </template>
        </page-select-group>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    data-test="cancel-button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-popover class="grow w-full" arrow placement="top" hover>
                    <vx-button
                        @click="submit"
                        data-test="submit-button"
                        :loading="submitting"
                        :disabled="groups.length === 0"
                        type="button"
                        block
                        size="lg"
                        color="primary"
                    >
                        Add Groups <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                    <template v-if="groups.length === 0" v-slot:content> You must select at least one Group </template>
                </vx-popover>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { VxButton, VxModal, VxPopover } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { catchRequestError } from '~/utils/request';
import { orgUnitGroupsClient } from '~/services';
import { pluralized } from '~/components/filters';
import PageSelectGroup from '~/components/general/PageSelectGroup.vue';
import { GROUP_STATUSES_CONFIGURABLE } from '~/constants/groupStatuses';

const visible = defineModel('visible', {
    type: Boolean,
    default: true,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnit: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['added']);

const toasted = useToasted();
const submitting = ref(false);

const groups = ref([]);

watch(visible, () => {
    groups.value = [];
});

const submit = async () => {
    if (submitting.value) {
        return;
    }

    if (!groups.value.length) {
        toasted.error('Select at least one group to add.');
        return;
    }

    if (groups.value.length > 50) {
        toasted.error('You can only select up to 50 groups.');
        return;
    }

    submitting.value = true;

    try {
        const results = await Promise.allSettled(
            groups.value.map((group) =>
                orgUnitGroupsClient.addOrgUnitGroup(props.teamId, props.orgUnit.id, group.group_id).then(() => group)
            )
        );

        const successfulResults = results
            .filter((result) => result.status === 'fulfilled')
            .map((result) => result.value);
        const failedResults = results.filter((result) => result.status === 'rejected');

        if (successfulResults.length) {
            toasted.success(
                `${pluralized('group', successfulResults.length, true)} ${
                    successfulResults.length === 1 ? 'was' : 'were'
                } successfully added.`
            );
        }

        if (failedResults.length) {
            toasted.error(`${pluralized('group', failedResults.length, true)} failed to be added.`);
        }

        emit('added', successfulResults);
        if (successfulResults.length && !failedResults.length) {
            visible.value = false;
        }

        // removed the successfully added groups
        groups.value = groups.value.filter(
            (group) => !successfulResults.some((successfulGroup) => successfulGroup.group_id == group.group_id)
        );
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};
</script>
